import React, { useState } from 'react';
import { validateSharePackage, toLinksLabRecipe, HELP_URL } from '../utils/share';
import SharePreview from './SharePreview';
import styles from './Share.module.css';

// ============================================================
// ShareImport — "Open shared recipe": read a package back into LinksLab
// ============================================================
// It only ever ADDS a recipe. Nothing already saved is touched, which is why
// there is no "replace current" path and no backup prompt.
export default function ShareImport({ calc, onClose }) {
  const [pkg,      setPkg]      = useState(null);
  const [errors,   setErrors]   = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [added,    setAdded]    = useState(null);

  const reset = () => { setPkg(null); setErrors([]); setWarnings([]); };

  const handleFile = (file) => {
    if (!file) return;
    reset();
    const reader = new FileReader();
    reader.onerror = () => setErrors(['Could not read that file.']);
    reader.onload = () => {
      const text = String(reader.result || '');
      let parsed = null;
      try { parsed = JSON.parse(text); } catch (e) { /* the validator says it better */ }

      // A PastramiLab package is valid, just not ours to open.
      if (parsed?.source?.app === 'PastramiLab' || parsed?.calculator?.app === 'PastramiLab') {
        setErrors(['This recipe is from PastramiLab — open it at pastramilab.mesarobles.com.']);
        return;
      }

      const result = validateSharePackage(text);
      setWarnings(result.warnings);
      if (!result.ok) { setErrors(result.errors); return; }
      setPkg(result.pkg);
    };
    reader.readAsText(file);
  };

  const handleAdd = () => {
    if (!pkg) return;
    const existing = calc.getSavedRecipes();
    const taken = (name) => existing.some(r => r.recipeName === name);
    const record = toLinksLabRecipe(pkg, { nameTaken: taken });

    // Save it alongside everything else, then load it into the calculator.
    const all = [...existing, record];
    try {
      localStorage.setItem('sc_recipes', JSON.stringify(all));
    } catch (e) {
      setErrors([`Could not save the recipe: ${e.message}`]);
      return;
    }
    calc.loadRecipe(record);
    setAdded(record.recipeName);
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>

        <div className={styles.header}>
          <div>
            <div className={styles.title}>Open shared recipe</div>
            <div className={styles.subtitle}>
              {added ? 'Added to My Recipes' : 'A .mesarobles.json file from Mesa Robles'}
            </div>
          </div>
          <button className={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        {added ? (
          <>
            <div className={styles.note}>
              <strong>{added}</strong> was added as a new recipe and is open now.
              Nothing you already had was changed.
            </div>
            <div className={styles.note}>
              Costs use your own ingredient prices; none were shared.
            </div>
          </>
        ) : (
          <>
            {errors.length > 0 && (
              <div className={styles.errors}>
                <div className={styles.errTitle}>This file cannot be opened</div>
                {errors.map((e, i) => <div key={i} className={styles.errItem}>{e}</div>)}
              </div>
            )}

            {warnings.length > 0 && (
              <div className={styles.warnings}>
                <div className={styles.warnTitle}>Worth reading</div>
                {warnings.map((w, i) => <div key={i} className={styles.warnItem}>{w}</div>)}
              </div>
            )}

            {!pkg ? (
              <div className={styles.picker}>
                <span className={styles.pickerIcon}>📥</span>
                <p className={styles.pickerText}>
                  Choose the file you downloaded from a Mesa Robles recipe page.
                  It is added as a new recipe — nothing you already have is changed.
                </p>
                <input
                  className={styles.fileInput}
                  type="file"
                  accept=".json,application/json"
                  onChange={e => handleFile(e.target.files?.[0])}
                />
              </div>
            ) : (
              <>
                <div className={styles.note}>
                  From {pkg.source?.app} · {pkg.display?.title}. Costs will use your
                  own ingredient prices; none were shared.
                </div>
                <SharePreview display={pkg.display} />
              </>
            )}
          </>
        )}

        <div className={styles.footer}>
          <a className={styles.helpLink} href={HELP_URL} onClick={(e) => {
            e.preventDefault();
            window.location.hash = 'share-help';
          }}>How sharing works</a>
          <div className={styles.footerRight}>
            <button className={styles.btnCancel} onClick={onClose}>
              {added ? 'Close' : 'Cancel'}
            </button>
            {!added && (
              <button className={styles.btnPrimary} onClick={handleAdd} disabled={!pkg}>
                Add as a new recipe
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
